import { useCallback, useEffect, useState } from 'react';

import { z } from 'zod/v4';

// Types
const ExpandedPreferenceStruct = z.enum(['collapsed', 'expanded']).nullable();
type ExpandedPreference = z.infer<typeof ExpandedPreferenceStruct>;

// Exports
type UseExpandedStoreProps = {
  key?: string;
  defaultTo?: ExpandedPreference;
};
export const useExpandedStore = ({
  key,
  defaultTo = null,
}: UseExpandedStoreProps) => {
  const read = useCallback(() => {
    if (!key) return defaultTo;
    const result = ExpandedPreferenceStruct.safeParse(
      sessionStorage.getItem(`finley-useExpandedStore.${key}`),
    );
    return result.error ? defaultTo : result.data ?? defaultTo;
  }, [defaultTo, key]);

  const [preference, setPreference] = useState(() => read());

  // Subscribe to storage changes, refresh state if needed
  useEffect(() => {
    if (!key) return () => undefined;
    const eventName = `useExpandedStore.${key}.storageUpdate`;
    const handler = () => setPreference(read());
    document.addEventListener(eventName, handler);
    return () => document.removeEventListener(eventName, handler);
  }, [key, read]);

  return {
    preference,
    set: (value: ExpandedPreference) => {
      if (!key) return;
      if (!value) {
        sessionStorage.removeItem(`finley-useExpandedStore.${key}`);
      } else {
        sessionStorage.setItem(`finley-useExpandedStore.${key}`, String(value));
      }
      // We modified storage, tell all instances of `useExpandedStore` to refresh their state
      document.dispatchEvent(
        new Event(`useExpandedStore.${key}.storageUpdate`),
      );
    },
  };
};
